import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { TribeMembershipDetails } from 'src/api/circles';
import { User, usersApi, UserWithTribeMembershipDetails } from 'src/api/user';

export type CircleRoleView = 'leader' | 'member';

const ROLE_VIEW_STORAGE_KEY = 'dunbar.circleRoleView';

const readStoredRoleView = (): CircleRoleView | null => {
  try {
    const stored = sessionStorage.getItem(ROLE_VIEW_STORAGE_KEY);
    return stored === 'leader' || stored === 'member' ? stored : null;
  } catch {
    return null;
  }
};

interface LoggedUserState {
  user: User | null;
  tribeDetails: TribeMembershipDetails[];
  commentCount: number;
  likeCount: number;
  roleViewOverride: CircleRoleView | null;

  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  initialized: boolean;
}

const initialState: LoggedUserState = {
  user: null,
  tribeDetails: [],
  commentCount: 0,
  likeCount: 0,
  roleViewOverride: readStoredRoleView(),

  status: 'idle',
  error: null,
  initialized: false,
};

export const fetchLoggedUser = createAsyncThunk<UserWithTribeMembershipDetails>(
  'user/fetchLoggedUser',
  async (): Promise<UserWithTribeMembershipDetails> => {
    return await usersApi.userMe();
  }
);

const loggedUserSlice = createSlice({
  name: 'loggedUser',
  initialState,
  reducers: {
    removeCircleById: (state, action) => {
      state.tribeDetails = state.tribeDetails.filter(
        (tribe) => tribe._id !== action.payload
      );
    },
    setRoleViewOverride: (state, action: PayloadAction<CircleRoleView>) => {
      state.roleViewOverride = action.payload;
      try {
        sessionStorage.setItem(ROLE_VIEW_STORAGE_KEY, action.payload);
      } catch {
        // Ignore storage errors in private browsing.
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLoggedUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(
        fetchLoggedUser.fulfilled,
        (state, action: PayloadAction<UserWithTribeMembershipDetails>) => {
          state.status = 'succeeded';
          state.user = action.payload.result;
          state.tribeDetails = action.payload.tribe_details;
          state.commentCount = action.payload.comment_count;
          state.likeCount = action.payload.like_count;
          state.initialized = true;
        }
      )
      .addCase(fetchLoggedUser.rejected, (state, action) => {
        state.status = 'failed';
        state.initialized = true;
        state.error = action.error.message || 'Failed to load user data';
      });
  },
});

export const selectCurrentUser = (state: { loggedUser: LoggedUserState }) =>
  state.loggedUser.user;
export const selectTribeDetails = (state: { loggedUser: LoggedUserState }) =>
  state.loggedUser.tribeDetails;
export const selectCommentCount = (state: { loggedUser: LoggedUserState }) =>
  state.loggedUser.commentCount;
export const selectLikeCount = (state: { loggedUser: LoggedUserState }) =>
  state.loggedUser.likeCount;
export const selectLoggedUserInitialized = (state: {
  loggedUser: LoggedUserState;
}) => state.loggedUser.initialized;

export const selectCurrentUserId = (state: { loggedUser: LoggedUserState }) =>
  state.loggedUser.user?._id;
export const selectRoleViewOverride = (state: {
  loggedUser: LoggedUserState;
}) => state.loggedUser.roleViewOverride;

export const selectFullUserInfo = createSelector(
  [
    (state: { loggedUser: LoggedUserState }) => state.loggedUser.user,
    (state: { loggedUser: LoggedUserState }) => state.loggedUser.commentCount,
    (state: { loggedUser: LoggedUserState }) => state.loggedUser.likeCount,
    (state: { loggedUser: LoggedUserState }) => state.loggedUser.tribeDetails,
  ],
  (user, commentCount, likeCount, tribeDetails) =>
    user
      ? {
          result: user,
          comment_count: commentCount,
          like_count: likeCount,
          tribe_details: tribeDetails,
        }
      : null
);

export const { removeCircleById, setRoleViewOverride } =
  loggedUserSlice.actions;

export default loggedUserSlice.reducer;
